import React from 'react';
import { makeStyles } from '@mui/styles';
import { TableRow, TableCell, Chip } from '@mui/material';
import MacronutrientPieChart from '../../MacronutrientPieChart/MacronutrientPieChart';
import { getChipStyles } from '../utils/macronutrientUtils';

const useStyles = makeStyles((theme) => ({
  mobileRow: {
    display: 'flex',
    flexDirection: 'row',
    padding: '8px 8px',
    gap: '16px',
    alignItems: 'flex-start',
    '& .MuiTableCell-root': {
      border: 'none',
      padding: '8px 8px',
    },
  },
  calorieRow: {
    borderBottom: `1px solid ${theme.palette.black12}`,
    '& .MuiTableCell-root': {
      border: 'none',
    },
  },
  mobileTitle: {
    width: '163px',
    maxWidth: '163px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    wordBreak: 'break-word',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '125%',
    letterSpacing: '0.17px',
    color: theme.palette.black87,
  },
  mobileContent: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    width: '134px',
    maxWidth: '134px',
  },
  mobileUnitValue: {
    display: 'flex',
    gap: '4px',
    paddingTop: '8px',
  },
  chip: {
    height: '34px',
    padding: '8px 12px',
    '& .MuiChip-label': {
      padding: 0,
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '125%',
      letterSpacing: '0.17px',
      whiteSpace: 'nowrap',
    },
  },
  unitRegular: theme.globalClasses.unitRegular,
  unitBold: theme.globalClasses.unitBold,
  mobilePieChartCell: {
    justifyContent: 'center',
    alignItems: 'center',
    '& .MuiTableCell-root': {
      border: 'none',
      padding: '0',
    },
  },
  pieChartRow: {
    paddingTop: '0',
    display: 'flex',
    justifyContent: 'center',
    '& .MuiTableCell-root': {
      border: 'none',
      padding: '0',
      height: 'auto',
    },
  },
}));

const MobileMacronutrientRow = ({
  name,
  current,
  currentPercentage,
  goal,
  goalPercentage,
  unit,
  isCalorieRow = false,
  allCurrentPercentages,
  calculatedChange,
  currentView = 'current',
  isLastRow = false,
}) => {
  const classes = useStyles({ isLastRow });

  const renderValue = () => {
    switch (currentView) {
      case 'current':
        return (
          <div className={classes.mobileUnitValue}>
            <span className={classes.unitBold}>{current}</span>
            <span className={classes.unitRegular}>{unit}</span>
            {currentPercentage !== undefined && <span className={classes.unitRegular}>({currentPercentage}%)</span>}
          </div>
        );
      case 'goal':
        return (
          <div className={classes.mobileUnitValue}>
            <span className={classes.unitBold}>{goal}</span>
            <span className={classes.unitRegular}>{unit}</span>
            {goalPercentage !== undefined && <span className={classes.unitRegular}>({goalPercentage}%)</span>}
          </div>
        );
      case 'change':
        return <div className={classes.mobileUnitValue}>{calculatedChange}</div>;
      default:
        return null;
    }
  };

  if (isCalorieRow) {
    return (
      <TableRow className={`${classes.mobileRow} ${classes.calorieRow}`}>
        <TableCell className={classes.mobileTitle}>{name}</TableCell>
        <TableCell className={classes.mobileContent}>
          <div className={classes.mobileUnitValue}>
            <span className={classes.unitBold}>{current}</span>
            <span className={classes.unitRegular}>{unit}</span>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <TableRow className={classes.mobileRow}>
        <TableCell className={classes.mobileTitle}>
          <Chip
            label={name}
            size="small"
            className={classes.chip}
            sx={getChipStyles(name)}
          />
        </TableCell>
        <TableCell className={classes.mobileContent}>{renderValue()}</TableCell>
      </TableRow>
      {name.toLowerCase() === 'protein' && allCurrentPercentages && currentView === 'current' && (
        <TableRow className={classes.pieChartRow}>
          <TableCell
            colSpan="2"
            className={classes.mobilePieChartCell}
          >
            <MacronutrientPieChart
              carbohydrates={allCurrentPercentages.carbohydrates}
              totalFat={allCurrentPercentages.totalFat}
              protein={allCurrentPercentages.protein}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default MobileMacronutrientRow;
